import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { Box, Button, CircularProgress, Collapse } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import 'moment/locale/pt-br';

import useFetch from '../../../../hooks/useFetch';
import { useAlert } from '../../../../hooks/useAlert';

import { instanceAxios } from '../../../../InstanceAxios';

import {
  CPF_PATTERN,
  RG_PATTERN,
  TELEFONE_PATTERN,
} from '../../../../consts/patterns';

import {
  DatePicker,
  Input,
  InputMask,
  Select,
} from '../../../../components/Form';

import { Container } from '../../../../components/Layouts/Container';

import schema from './schema';

import { Title, Centered } from './styles';

const MAX_DATE = moment().subtract(15, 'years').format('YYYY-MM-DD');
const MIN_DATE = moment().subtract(130, 'years').format('YYYY-MM-DD');

function DadosPessoais() {
  const { isLoading, isError, data } = useFetch('/usuario/show');
  const { data: segmentos } = useFetch('/segmentosatuacao');

  const { alert, openAlert, closeAlert } = useAlert();

  const [segmentoOptions, setSegmentoOptions] = useState([]);
  const [formSubmiting, setFormSubmiting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty, dirtyFields },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  // atualiza os dados do usuário quando a requisicao é concluida
  useEffect(() => {
    reset(data);
  }, [data]);

  // faz tratamento dos dados de segmento
  useEffect(() => {
    if (segmentos?.length > 0) {
      const options = segmentos.map((segmento) => ({
        value: segmento.id,
        label: segmento.nome,
      }));

      setSegmentoOptions(options);
    }
  }, [segmentos]);

  async function onSubmit(formData) {
    try {
      setFormSubmiting(true);

      const updatedData = {
        ...formData,
      };

      // remove campos que não foram modificados
      Object.entries(updatedData).forEach(([key]) => {
        if (!Object(dirtyFields).hasOwnProperty(key)) {
          delete updatedData[key];
        }
      });

      // trata data de nascimento para o formato do banco
      if (updatedData?.data_nascimento) {
        updatedData.data_nascimento = moment(
          updatedData.data_nascimento
        ).format('YYYY-MM-DD');
      }

      const token = localStorage.getItem('toobiauth');

      await instanceAxios(token).put('/usuario/update', updatedData);

      openAlert('Dados atualizados com sucesso!');

      reset(formData);
    } catch (err) {
      console.log(err);

      openAlert('Erro ao atualizar dados!', 'error');
    } finally {
      setFormSubmiting(false);
    }
  }

  return (
    <Container isLoading={isLoading} isError={isError}>
      <Collapse in={alert.open}>
        <Alert severity={alert.severity} onClick={closeAlert}>
          {alert.message}
        </Alert>
      </Collapse>
      <Title>Dados pessoais</Title>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Input
            control={control}
            id="nome"
            name="nome"
            label="Nome completo"
            error={!!errors?.nome}
            helperText={errors?.nome ? errors.nome.message : ''}
            variant="outlined"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            control={control}
            id="email"
            name="email"
            label="Email"
            error={!!errors?.email}
            helperText={errors?.email ? errors.email.message : ''}
            variant="outlined"
            disabled
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <InputMask
            control={control}
            id="cpf"
            name="cpf"
            label="CPF"
            variant="outlined"
            pattern={CPF_PATTERN}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <InputMask
            control={control}
            id="rg"
            name="rg"
            label="RG"
            pattern={RG_PATTERN}
            variant="outlined"
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            control={control}
            id="data_nascimento"
            name="data_nascimento"
            label="Data de Nascimento"
            variant="outlined"
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            control={control}
            id="nomeMae"
            name="nome_mae"
            label="Nome Mãe"
            variant="outlined"
            fullWidth
          />
        </Grid>
        <Grid item xs={3}>
          <Select
            control={control}
            id="sexo"
            name="sexo"
            label="Sexo"
            variant="outlined"
            select
            options={[
              { value: 'Masculino', label: 'Masculino' },
              { value: 'Feminino', label: 'Feminino' },
            ]}
            fullWidth
          />
        </Grid>
        <Grid item xs={3}>
          <InputMask
            control={control}
            id="telefone"
            name="telefone"
            label="Telefone"
            variant="outlined"
            pattern={TELEFONE_PATTERN}
            error={!!errors?.telefone}
            helperText={errors?.telefone ? errors.telefone.message : ''}
          />
        </Grid>
        {segmentoOptions.length > 0 && (
          <Grid item xs={6}>
            <Select
              control={control}
              id="segmentoatuacao"
              name="id_segmento_atuacao"
              label="Segmento"
              options={segmentoOptions}
              variant="outlined"
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <Centered>
        <Box marginTop={4}>
          <Button
            onClick={handleSubmit(onSubmit)}
            size="medium"
            variant="contained"
            color="primary"
            disabled={!isValid || !isDirty || formSubmiting}
          >
            {formSubmiting ? (
              <CircularProgress color="primary" size={25} />
            ) : (
              'Atualizar dados'
            )}
          </Button>
        </Box>
      </Centered>
    </Container>
  );
}

export default DadosPessoais;
